import React from "react";
import { AlertData, EnhancedTableProps } from "./AlertsUtils/alertsInterfaces";
import {
  Box,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
} from "@mui/material";
import { HeadCell } from "./AlertsUtils/alertsInterfaces";
import { visuallyHidden } from "@mui/utils";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { useAppStore } from "../../../appStore";
import { alertsTableSeenColumnSx } from "./AlertsUtils/alertsTableResize";

export const EnhancedAlertsTableHead = (props: EnhancedTableProps) => {
  const { onRequestSort, orderBy, order, showVlmVerdictColumn } = props;

  // Move state and handlers inside the component
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation(); // Stop event propagation to prevent table sorting
    setAnchorEl(event.currentTarget);
  };
  const handleClose = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation(); // Stop event propagation to prevent table sorting
    setAnchorEl(null);
  };

  const { setDateMMDDYYYY } = useAppStore();

  // Define headCells inside the component so it can access the state and handlers
  const headCells = [
    {
      id: "display_name",
      numeric: false,
      label: "Site",
    },
    {
      id: "camera_name",
      numeric: false,
      label: "Camera",
    },
    {
      id: "formatted_date",
      numeric: true,
      label: (
        <div className="flex shrink-0 whitespace-nowrap">
          <>{`Site Time`}</>
          <div>
            <IconButton
              aria-label="more"
              id="long-button"
              aria-controls={open ? "long-menu" : undefined}
              aria-expanded={open ? "true" : undefined}
              aria-haspopup="true"
              onClick={handleClick}
              sx={{
                width: "18px",
                height: "18px",
                padding: "0px",
              }}
            >
              <MoreVertIcon sx={{ fontSize: "18px" }} />
            </IconButton>
            <Menu
              id="long-menu"
              MenuListProps={{
                "aria-labelledby": "long-button",
              }}
              anchorOrigin={{
                vertical: "top",
                horizontal: "left",
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "left",
              }}
              anchorEl={anchorEl}
              open={open}
              onClose={handleClose}
              slotProps={{
                paper: {
                  style: {
                    maxHeight: 48 * 4.5,
                    width: "20ch",
                  },
                },
              }}
            >
              <MenuItem
                key="mm-dd-yyyy"
                onClick={(event) => {
                  setDateMMDDYYYY(true);
                  event.stopPropagation(); // Stop event propagation to prevent table sorting
                  handleClose(event);
                }}
              >
                MM/DD/YYYY
              </MenuItem>
              <MenuItem
                key="dd-mm-yyyy"
                onClick={(event) => {
                  setDateMMDDYYYY(false);
                  event.stopPropagation(); // Stop event propagation to prevent table sorting
                  handleClose(event);
                }}
              >
                DD/MM/YYYY
              </MenuItem>
            </Menu>
          </div>
        </div>
      ),
    },
    {
      id: "alert_labels",
      numeric: false,
      label: "Type",
    },
    {
      id: "detection_type",
      numeric: false,
      label: "Detection",
    },
    // DoubleTake verdict column (ENG-1756). Ranked sort in shared comparator
    // (ENG-1820) so prev/next matches the table.
    ...(showVlmVerdictColumn
      ? [
          {
            id: "vlm_verdict",
            numeric: false,
            label: "DT Verdict",
          },
        ]
      : []),
  ] as Array<HeadCell & { label: string | JSX.Element; sortable?: boolean }>;

  const createSortHandler =
    (property: keyof AlertData) => (event: React.MouseEvent<unknown>) => {
      onRequestSort(event, property);
    };

  return (
    <TableHead>
      <TableRow>
        <TableCell
          key={"seen"}
          align="left"
          sortDirection={orderBy === "seen" ? order : false}
          sx={alertsTableSeenColumnSx}
        >
          <TableSortLabel
            active={orderBy === "seen"}
            direction={orderBy === "seen" ? order : "asc"}
            onClick={createSortHandler("seen")}
          >
            {orderBy === "seen" ? (
              <Box component="span" sx={visuallyHidden}>
                {order === "desc" ? "sorted descending" : "sorted ascending"}
              </Box>
            ) : null}
          </TableSortLabel>
        </TableCell>
        {headCells.map((headCell) => (
          <TableCell
            key={headCell.id}
            align="left"
            sortDirection={orderBy === headCell.id ? order : false}
            style={{
              width: `20%`,
            }}
            sx={{
              fontFamily: "mulish",
              fontWeight: 700,
              fontSize: 11,
            }}
          >
            {headCell.sortable === false ? (
              headCell.label
            ) : (
              <TableSortLabel
                active={orderBy === headCell.id}
                direction={orderBy === headCell.id ? order : "asc"}
                onClick={createSortHandler(headCell.id)}
              >
                {headCell.label}
                {orderBy === headCell.id ? (
                  <Box component="span" sx={visuallyHidden}>
                    {order === "desc"
                      ? "sorted descending"
                      : "sorted ascending"}
                  </Box>
                ) : null}
              </TableSortLabel>
            )}
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
};
